import Vue from 'vue';
import axios from 'axios';
import Router from 'vue-router';
// eslint-disable-next-line import/no-cycle
import store from '@/store';
import EventBus from '@/helpers/event-bus';

Vue.use(Router);

/**
 * Decode JWT token without verification (just extract payload)
 * Used to get orgId from tokens even if they're expired
 * Handles UTF-8 encoded payloads (ASCII, Unicode, emoji, etc.)
 */
function decodeJWT(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

    // Convert base64 to binary string
    const binaryString = atob(base64);

    // Convert binary string to Uint8Array
    const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));

    // Decode UTF-8 bytes to string
    const jsonPayload = new TextDecoder().decode(bytes);

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.log('Failed to decode JWT token:', error);
    return null;
  }
}

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('./views/Recover.vue'),
      meta: { requiresAuth: true, title: 'Update Your Card' },
    },
    {
      path: '/update',
      name: 'recover',
      component: () => import('./views/Recover.vue'),
      meta: { requiresAuth: true, title: 'Update Your Card' },
    },
    {
      path: '/reactivate',
      name: 'reactivate',
      component: () => import('./views/Reactivate.vue'),
      meta: { requiresAuth: true, title: 'Reactivate Your Subscription' },
    },
    {
      path: '/v/:shortToken',
      name: 'verify-recover',
      component: () => import('./views/Recover.vue'),
      meta: { requiresAuth: true, shortToken: true, title: 'Update Your Card' },
    },
    {
      path: '/r/:shortToken',
      name: 'verify-reactivate',
      component: () => import('./views/Reactivate.vue'),
      meta: { requiresAuth: true, shortToken: true, title: 'Reactivate Your Subscription' },
    },
    {
      path: '/demo/recover',
      name: 'demo-recover',
      component: () => import('./views/RecoverDemo.vue'),
      meta: { requiresAuth: false, title: 'Update Your Card' },
    },
    {
      path: '/demo/reactivate',
      name: 'demo-reactivate',
      component: () => import('./views/ReactivationDemo.vue'),
      meta: { requiresAuth: false, title: 'Reactivate Your Subscription' },
    },
    {
      path: '/error',
      name: 'error',
      component: () => import('./views/Error.vue'),
      meta: { requiresAuth: false, title: 'Error' },
    },
    /*
    {
      path: '/test-logos',
      name: 'test-logos',
      component: () => import('./views/TestLogos.vue'),
      meta: { requiresAuth: false },
    },
    */
  ],
});

// perform auth check
router.beforeEach(async (to, from, next) => {
  // Set page title
  document.title = to.meta.title;

  if (to.meta.shortToken) {
    await store.dispatch('CLEANUP');
    const token = to.params.shortToken;
    axios.defaults.headers.common['x-ck-short-token'] = `${token}`;
    await store.dispatch('SYNC_ACCOUNT');

    // Store orgId from successful account load (for short token auth)
    if (store.state.account?._id) {
      sessionStorage.setItem('pageLoadOrgId', store.state.account._id);
    }

    return next();
  }
  if (to.meta.requiresAuth === true) {
    if (to.query?.auth?.length > 1) {
      await store.dispatch('CLEANUP');
      // persist jwt for future requests & create auth header
      const token = to.query.auth;
      localStorage.setItem('token', `${token}`);
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;

      // Extract and store orgId from token (even if expired/invalid)
      // This allows error pages to show correct org branding
      const decoded = decodeJWT(token);
      if (decoded?._id) {
        sessionStorage.setItem('pageLoadOrgId', decoded._id);
      }
    }
    if (to.params?.shortToken?.length > 1) {
      // persist jwt for future requests & create auth header
      const token = to.params.shortToken;
      axios.defaults.headers.common['x-ck-short-token'] = `${token}`;
    }
    try {
      await store.dispatch('SYNC_ACCOUNT');

      // Store orgId from successful account load (for short tokens and other auth methods)
      if (store.state.account?._id) {
        sessionStorage.setItem('pageLoadOrgId', store.state.account._id);
      }

      return next();
    } catch (error) {
      console.log('error', error);
      EventBus.$emit('logIssue', store?.state?.account);
      // Auth error. Remove token and clean up
      // Note: We keep pageLoadOrgId in sessionStorage for error page
      await store.dispatch('CLEANUP');
      return next();
    }
  }
  return next();
});

export default router;
